import { z } from "zod";

/**
 * Widget-oriented description of one parameter, derived from the zod schema
 * plus `.meta()` hints. This is what the generated UI and the API consume;
 * design code never builds it by hand.
 */
export type ParamType = "number" | "int" | "bool" | "select" | "string";

export interface ParamSpec {
  name: string;
  label: string;
  type: ParamType;
  default: unknown;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: { value: string; label: string }[];
  unit?: string;
  /** Normalised [x, y, z] in the design's bounding box for floating labels. */
  anchor?: [number, number, number];
  group?: string;
  /** Name of a widget override registered by the brand. */
  widget?: string;
  advanced?: boolean;
}

/** Hints that ride along in `.meta()` on a zod field. */
export interface ParamMeta {
  [key: string]: unknown;
  label?: string;
  description?: string;
  unit?: string;
  step?: number;
  anchor?: [number, number, number];
  group?: string;
  widget?: string;
  advanced?: boolean;
  /** Labels for enum options. */
  optionLabels?: Record<string, string>;
}

type JsonSchemaProp = {
  type?: string | string[];
  anyOf?: JsonSchemaProp[];
  enum?: unknown[];
  const?: unknown;
  default?: unknown;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  multipleOf?: number;
  title?: string;
  description?: string;
  [k: string]: unknown;
};

export interface JsonSchemaObject {
  type: "object";
  properties: Record<string, JsonSchemaProp>;
  required?: string[];
  additionalProperties?: boolean;
  [k: string]: unknown;
}

function humanize(name: string): string {
  return name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function baseType(prop: JsonSchemaProp): { t?: string; enumValues?: unknown[] } {
  if (prop.enum) return { t: "enum", enumValues: prop.enum };
  const t = Array.isArray(prop.type) ? prop.type.find((x) => x !== "null") : prop.type;
  if (t) return { t };
  if (prop.anyOf) {
    for (const alt of prop.anyOf) {
      const inner = baseType(alt);
      if (inner.t) return inner;
    }
  }
  return {};
}

/** Convert a zod object schema into a JSON schema (input side, so defaults make fields optional). */
export function toJsonSchema(schema: z.ZodObject): JsonSchemaObject {
  const js = z.toJSONSchema(schema, { io: "input", unrepresentable: "any" }) as JsonSchemaObject;
  return js;
}

/** Derive the parameter specs a UI needs from the schema and its metadata. */
export function introspectParams(schema: z.ZodObject): ParamSpec[] {
  const js = toJsonSchema(schema);
  const specs: ParamSpec[] = [];
  for (const [name, prop] of Object.entries(js.properties)) {
    const field = schema.shape[name] as z.ZodType | undefined;
    const meta = (field?.meta?.() ?? {}) as ParamMeta;
    const { t, enumValues } = baseType(prop);
    const spec: ParamSpec = {
      name,
      label: meta.label ?? prop.title ?? humanize(name),
      type: "string",
      default: prop.default,
    };
    const description = meta.description ?? prop.description;
    if (description) spec.description = description;

    if (t === "integer") spec.type = "int";
    else if (t === "number") spec.type = "number";
    else if (t === "boolean") spec.type = "bool";
    else if (t === "enum") {
      spec.type = "select";
      spec.options = (enumValues ?? []).map((v) => ({
        value: String(v),
        label: meta.optionLabels?.[String(v)] ?? humanize(String(v)),
      }));
    }

    if (spec.type === "number" || spec.type === "int") {
      if (prop.minimum !== undefined) spec.min = prop.minimum;
      else if (prop.exclusiveMinimum !== undefined) spec.min = prop.exclusiveMinimum;
      if (prop.maximum !== undefined) spec.max = prop.maximum;
      else if (prop.exclusiveMaximum !== undefined) spec.max = prop.exclusiveMaximum;
      if (meta.step !== undefined) spec.step = meta.step;
      else if (prop.multipleOf !== undefined) spec.step = prop.multipleOf;
      else if (spec.type === "int") spec.step = 1;
    }
    if (meta.unit) spec.unit = meta.unit;
    if (meta.anchor) spec.anchor = meta.anchor;
    if (meta.group) spec.group = meta.group;
    if (meta.widget) spec.widget = meta.widget;
    if (meta.advanced) spec.advanced = true;
    specs.push(spec);
  }
  return specs;
}

/** Field helpers so designs read well: `p.mm("Width", 20, { min: 5, max: 60 })`. */
export const p = {
  number(label: string, def: number, o: { min?: number; max?: number; step?: number; unit?: string; anchor?: [number, number, number]; group?: string; description?: string } = {}) {
    let s = z.number();
    if (o.min !== undefined) s = s.min(o.min);
    if (o.max !== undefined) s = s.max(o.max);
    const meta: ParamMeta = { label };
    if (o.step !== undefined) meta.step = o.step;
    if (o.unit) meta.unit = o.unit;
    if (o.anchor) meta.anchor = o.anchor;
    if (o.group) meta.group = o.group;
    if (o.description) meta.description = o.description;
    return s.default(def).meta(meta);
  },
  mm(label: string, def: number, o: { min?: number; max?: number; step?: number; anchor?: [number, number, number]; group?: string; description?: string } = {}) {
    return p.number(label, def, { ...o, unit: "mm" });
  },
  deg(label: string, def: number, o: { min?: number; max?: number; step?: number; anchor?: [number, number, number]; group?: string; description?: string } = {}) {
    return p.number(label, def, { ...o, unit: "°" });
  },
  int(label: string, def: number, o: { min?: number; max?: number; step?: number; unit?: string; anchor?: [number, number, number]; group?: string; description?: string } = {}) {
    let s = z.number().int();
    if (o.min !== undefined) s = s.min(o.min);
    if (o.max !== undefined) s = s.max(o.max);
    const meta: ParamMeta = { label, step: o.step ?? 1 };
    if (o.unit) meta.unit = o.unit;
    if (o.anchor) meta.anchor = o.anchor;
    if (o.group) meta.group = o.group;
    if (o.description) meta.description = o.description;
    return s.default(def).meta(meta);
  },
  studs(label: string, def: number, o: { min?: number; max?: number; anchor?: [number, number, number]; group?: string; description?: string } = {}) {
    return p.int(label, def, { ...o, unit: "studs" });
  },
  bool(label: string, def: boolean, o: { anchor?: [number, number, number]; group?: string; description?: string } = {}) {
    const meta: ParamMeta = { label };
    if (o.anchor) meta.anchor = o.anchor;
    if (o.group) meta.group = o.group;
    if (o.description) meta.description = o.description;
    return z.boolean().default(def).meta(meta);
  },
  select<const T extends readonly [string, ...string[]]>(label: string, options: T, def: T[number], o: { labels?: Partial<Record<T[number], string>>; anchor?: [number, number, number]; group?: string; description?: string } = {}) {
    const meta: ParamMeta = { label };
    if (o.labels) meta.optionLabels = o.labels as Record<string, string>;
    if (o.anchor) meta.anchor = o.anchor;
    if (o.group) meta.group = o.group;
    if (o.description) meta.description = o.description;
    return z.enum(options as unknown as [string, ...string[]]).default(def).meta(meta) as unknown as z.ZodDefault<z.ZodEnum<{ [K in T[number]]: K }>>;
  },
};

/**
 * Carry a parameter set over to another design (a family sibling) or another
 * version: keep only the params `spec` knows, drop values of the wrong type,
 * clamp numbers into range (rounding ints) and drop select values that are not
 * options. What is dropped falls back to the target's defaults.
 */
export function portParams(params: Record<string, unknown>, spec: ParamSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of spec) {
    if (!(s.name in params)) continue;
    const v = params[s.name];
    if (s.type === "number" || s.type === "int") {
      if (typeof v !== "number" || !Number.isFinite(v)) continue;
      let n = s.type === "int" ? Math.round(v) : v;
      if (s.min !== undefined) n = Math.max(s.min, n);
      if (s.max !== undefined) n = Math.min(s.max, n);
      out[s.name] = n;
    } else if (s.type === "bool") {
      if (typeof v === "boolean") out[s.name] = v;
    } else if (s.type === "select") {
      if (s.options?.some((o) => o.value === v)) out[s.name] = v;
    } else if (typeof v === "string" || typeof s.default !== "string") {
      // Free text, or a type the spec doesn't model (arrays, objects): the schema still validates it.
      out[s.name] = v;
    }
  }
  return out;
}
